import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Video, TrendingUp, MessageCircle } from 'lucide-react';

const ServicesPreview = () => {
  const services = [
    {
      icon: Video,
      title: "UGC Creation",
      description: "Short-form video, product reviews, lifestyle shoots that blend seamlessly into feeds but drive action.",
      cta: "See My UGC Work",
      link: "https://vibewithsimm.my.canva.site/ugc",
      bgColor: "#ffe6fe"
    },
    {
      icon: TrendingUp,
      title: "Social Media Strategy", 
      description: "Content calendars, growth frameworks, and strategies that actually work (not just sound good).",
      cta: "View Case Studies",
      link: "https://vibewithsimm.my.canva.site/daghmugxxpi",
      bgColor: "#fdecff"
    },
    {
      icon: MessageCircle,
      title: "Consulting Calls",
      description: "1:1 brand audits, campaign brainstorming, and creative direction to get you unstuck.",
      cta: "Book a Call",
      link: "https://calendly.com/simmbedi-sb/30min",
      bgColor: "#dee4d0"
    }
  ];

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          {/* Section Title */}
          <div className="text-sm font-medium text-pink-500 flex items-center justify-center gap-2 mb-4">
            🚀 What I Do
          </div>
          
          {/* Headline */}
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Services that go beyond 
            <span className="relative">
              <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent"> likes</span>
              {/* Heart doodle */}
              <svg className="absolute -top-2 -right-8 w-6 h-6 text-pink-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </span>
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            
            return (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white"
              >
                <CardContent className="p-8 text-center">
                  {/* Icon */}
                  <div 
                    className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: service.bgColor }}
                  >
                    <IconComponent className="h-8 w-8 text-gray-700" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  {/* CTA */}
                  <div>
                    {service.link ? (
                      <a 
                        href={service.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-gray-700 hover:text-gray-900 font-medium group transition-colors duration-200"
                      >
                        {service.cta}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </a>
                    ) : (
                      <Button 
                        variant="ghost" 
                        className="text-gray-700 hover:text-gray-900 p-0 font-medium group"
                      >
                        {service.cta}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="text-center mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            {/* Content */}
            <div 
              className="p-8 rounded-2xl"
              style={{
                background: `linear-gradient(135deg, #ffe6fe 0%, #fdecff 50%, #dee4d0 100%)`
              }}
            >
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Not sure which service fits your needs?
              </h3>
              <p className="text-gray-700 mb-6">
                Let's chat about your goals and I'll recommend the perfect approach
              </p>
              <Button 
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <a href="https://calendly.com/simmbedi-sb/15-min-discovery-call" target="_blank" rel="noopener noreferrer" className="flex items-center">
                  Book a Free Discovery Call
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="transform rotate-2 hover:rotate-1 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_identity-forge-17/artifacts/xrlr5xcd_IMG_2926.jpg" 
                  alt="Simm Bedi creating content"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-full px-4 py-2 shadow-lg border-2 border-pink-200">
                <span className="text-sm font-medium text-gray-800">📸 Creating Magic</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;